import {
  ComponentPropsWithoutRef,
  forwardRef,
  MouseEvent,
  KeyboardEvent,
  useRef,
  FocusEvent,
  RefObject,
} from "react";
import styles from "./Popover.module.scss";
import cx from "classnames";
import { useMergeRefs } from "../../hooks";
import { usePopoverContext } from "./usePopoverContext";
import { compareElements } from "../../internal";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import {
  PopoverLegacyContent,
  PopoverLegacyContentProps,
} from "../PopoverLegacy";

/**
 * Props for the PopoverContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PopoverContentProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "popover"
> & {
  /**
   * Reference to the scrollable content area
   */
  scrollerRef?: RefObject<HTMLDivElement>;
};

/**
 * PopoverContent component for the main popover content area.
 *
 * Features:
 * - Automatic positioning and sizing based on trigger
 * - Screen boundary detection and fitting
 * - Scroll handling with custom scroller reference
 * - Focus management and keyboard navigation
 * - Hover interaction support for tooltip mode
 * - Escape key handling for closing
 * - Tab key trapping for modal popovers
 * - Arrow/caret indicator with automatic positioning
 * - Legacy popover support with fallback
 * - Context integration with parent popover
 * - Accessibility support with proper ARIA roles
 *
 * @example
 * <Popover.Content>
 *   <p>This is the popover content</p>
 *   <Popover.Close>Close</Popover.Close>
 * </Popover.Content>
 *
 * @example
 * <Popover.Content scrollerRef={scrollRef}>
 *   <div style={{ height: '200px', overflow: 'auto' }}>
 *     {longContent}
 *   </div>
 * </Popover.Content>
 */
export const PopoverContent = forwardRef<HTMLDivElement, PopoverContentProps>(
  function PopoverContent(props, ref) {
    const {
      children,
      className,
      style,
      onMouseEnter,
      onMouseLeave,
      onKeyDown,
      onFocus,
      onBlur,
      onScroll,
      onScrollCapture,
      scrollerRef,
      ...rest
    } = props;
    const context = usePopoverContext();
    const popoverClassNames = cx(className, styles["content"], {
      [styles["no-padding"]]: context?.noPadding,
    });
    const childrenRef = useRef<HTMLDivElement>(null);
    const combinedStyles = {
      ...style,
      ...context?.popover.style,
    };

    const focusables = context?.focusables;
    const onMouseEnterHandler = (e: MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(e);
      if (!context?.openOnHover) return;
      context.popover.show();
    };
    const onMouseLeaveHandler = (e: MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      if (!context?.openOnHover) return;
      context.popover.close();
    };
    const onFocusHandler = (e: FocusEvent<HTMLDivElement>) => {
      onFocus?.(e);
      context?.popover.show();
    };
    const onBlurHandler = (e: FocusEvent<HTMLDivElement>) => {
      onBlur?.(e);
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(e);
      if (e.code === "Escape" && !context?.disableCloseOnEscape) {
        context?.popover.close();
      }
      if (!context?.modal) {
        if (context?.controlled) {
          return;
        }
        if (e.code === "Tab" && !e.shiftKey) {
          if (
            focusables &&
            compareElements(
              (context.rootNode as ShadowRoot | Document)
                ?.activeElement as HTMLElement,
              (focusables as HTMLElement[])[focusables.length - 1],
              ["tabindex"],
            )
          ) {
            context.popover.close();
          }
        }
        if (e.code === "Tab" && e.shiftKey) {
          if (
            focusables &&
            compareElements(
              (context.rootNode as ShadowRoot | Document)
                .activeElement as HTMLElement,
              (focusables as HTMLElement[])[0],
              ["tabindex"],
            )
          ) {
            context.popover.close();
          }
        }
        return;
      }
      if (e.code === "Tab" && !e.shiftKey) {
        if (
          focusables &&
          compareElements(
            (context.rootNode as ShadowRoot | Document)
              ?.activeElement as HTMLElement,
            (focusables as HTMLElement[])[focusables.length - 1],
            ["tabindex"],
          )
        ) {
          if (context.disableTriggerFocus) {
            focusables[0].focus();
          } else {
            context.invoker.element?.focus();
            context.popover.show();
          }
          e.preventDefault();
        }
      }
      if (e.code === "Tab" && e.shiftKey) {
        if (
          focusables &&
          compareElements(
            (context.rootNode as ShadowRoot | Document)
              .activeElement as HTMLElement,
            (focusables as HTMLElement[])[0],
            ["tabindex"],
          )
        ) {
          if (context.disableTriggerFocus) {
            (focusables[focusables.length - 1] as HTMLButtonElement).focus();
          } else {
            context.invoker.element?.focus();
            context.popover.show();
          }
          e.preventDefault();
        }
      }
    };

    const manualPopover =
      context?.controlled ||
      context?.disableCloseOnClickOutside ||
      context?.disableCloseOnEscape ||
      context?.openOnHover;

    const popoverContentRef = useMergeRefs([context?.popover.ref, ref]);
    const popoverScrollerRef = useMergeRefs([childrenRef, scrollerRef]);

    if (!supportsPopover())
      return (
        <PopoverLegacyContent
          {...(props as PopoverLegacyContentProps)}
          ref={ref}
        />
      );

    return (
      <div
        popover={manualPopover ? "manual" : "auto"}
        id={context?.popover.id}
        data-anv="popover-content"
        className={popoverClassNames}
        ref={popoverContentRef}
        style={combinedStyles}
        onMouseEnter={onMouseEnterHandler}
        onMouseLeave={onMouseLeaveHandler}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        role="region"
        {...rest}
      >
        <div
          className={styles.scroller}
          role="presentation"
          tabIndex={-1}
          ref={popoverScrollerRef}
          key={context?.popover.key}
          onKeyDown={onKeyDownHandler}
          onScroll={onScroll}
          onScrollCapture={onScrollCapture}
        >
          {children}
        </div>
        {!context?.disableCaret && (
          <span className={styles["arrow"]} ref={context?.popover.arrowRef}>
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14">
              <path stroke="none" d="M0,0 H14 L7,7 Q7,7 7,7 Z"></path>
              <clipPath id=":r9:">
                <rect x="0" y="0" width="14" height="14"></rect>
              </clipPath>
            </svg>
          </span>
        )}
      </div>
    );
  },
);
